import { getCustomColDefProperties, type SecurityMarketDataRequestParamsContext } from '@talos/kyoko';
import type { ColDef, Column } from 'ag-grid-community';
import { get } from 'lodash-es';
import { getPriceLadderDepth } from '../expandSecurityToPriceGridRows';
import {
  DEFAULT_PRICE_GRID_PRICE_INCREMENT,
  isPriceLadderMode,
  PriceGridLadderType,
  type PriceGridLadderType as PriceGridLadderTypeValue,
} from '../ladder';
import { PriceGridEntity } from '../PriceGridEntity';

interface PriceGridMarketDataBackedColumnParams<TColumnParams> {
  priceGridMarketDataColumnType: string;
  priceGridMarketDataColumnParams?: TColumnParams;
}

function getPriceGridMarketDataBackedColumnParams<TColumnParams>(colDef: ColDef | undefined) {
  return colDef?.cellRendererParams as PriceGridMarketDataBackedColumnParams<TColumnParams> | undefined;
}

export interface PriceGridMarketDataRequestConfig {
  ladderType: PriceGridLadderTypeValue;
  priceIncrement: string;
}

export function createGetPriceGridMarketDataRequestParams({
  ladderType,
  priceIncrement,
}: PriceGridMarketDataRequestConfig) {
  return (context: SecurityMarketDataRequestParamsContext) =>
    getPriceGridMarketDataRequestParams(context, { ladderType, priceIncrement });
}

export function getPriceGridMarketDataRequestParams(
  { data }: SecurityMarketDataRequestParamsContext,
  { ladderType, priceIncrement }: PriceGridMarketDataRequestConfig = {
    ladderType: PriceGridLadderType.Ladder,
    priceIncrement: DEFAULT_PRICE_GRID_PRICE_INCREMENT,
  }
) {
  if (!(data instanceof PriceGridEntity)) {
    return undefined;
  }

  if (isPriceLadderMode(ladderType)) {
    return {
      Depth: getPriceLadderDepth(data.sourceItem),
      PriceIncrement: priceIncrement,
    };
  }

  const sizeBuckets = data.sourceItem.security.SizeBuckets?.filter(
    (sizeBucket): sizeBucket is string => typeof sizeBucket === 'string'
  );
  const resolvedSizeBuckets =
    sizeBuckets && sizeBuckets.length > 0 ? sizeBuckets : [data.sourceItem.security.NormalSize];
  return resolvedSizeBuckets.length > 0 ? { SizeBuckets: resolvedSizeBuckets } : undefined;
}

export function getPriceGridCellSymbol(column: Column, item: PriceGridEntity) {
  const symbolPath = column.getColDef()?.field;
  if (!symbolPath) {
    return undefined;
  }

  const symbol = get(item, symbolPath);
  return typeof symbol === 'string' && symbol.length > 0 ? symbol : undefined;
}

export function getPriceGridColumnParams<TColumnParams>(column: Column, columnType: string): TColumnParams | undefined {
  return getPriceGridColumnParamsFromColDef<TColumnParams>(column.getColDef(), columnType);
}

export function getPriceGridColumnParamsFromColDef<TColumnParams>(
  colDef: ColDef | undefined,
  columnType: string
): TColumnParams | undefined {
  const customColDefProperties = getCustomColDefProperties(colDef);

  if (customColDefProperties?.talosColumnType === columnType) {
    return customColDefProperties.talosColumnParams as TColumnParams | undefined;
  }

  const marketDataBackedColumnParams = getPriceGridMarketDataBackedColumnParams<TColumnParams>(colDef);
  return marketDataBackedColumnParams?.priceGridMarketDataColumnType === columnType
    ? marketDataBackedColumnParams.priceGridMarketDataColumnParams
    : undefined;
}

export function getPriceGridColumnTypeFromColDef(colDef: ColDef | undefined): string | undefined {
  const customColDefProperties = getCustomColDefProperties(colDef);
  const marketDataBackedColumnParams = getPriceGridMarketDataBackedColumnParams<unknown>(colDef);

  return marketDataBackedColumnParams?.priceGridMarketDataColumnType ?? customColDefProperties?.talosColumnType;
}
